import {
  APIModelAttributes,
  APIOrgTripleAttributes,
  APIWorkspaceAttributes,
  AssetFolder,
  DirectedTriple,
  MetaModelTriple,
  TripleDirection,
} from '@ardoq/api-types';
import { PermissionContext } from '@ardoq/access-control';
import { StreamCollection } from 'streams/utils/streamUtils';
import {
  toTriplesWithContext,
  TripleWithContext,
} from './toTriplesWithContext';
import { uniqBy } from 'lodash';

type GetAllRelevantTriplesArgs = {
  componentTypeName: string;
  permissionContext: PermissionContext;
  orgTriples: APIOrgTripleAttributes[];
  inferredTriples: MetaModelTriple[];
  models: StreamCollection<APIModelAttributes>;
  workspaces: StreamCollection<APIWorkspaceAttributes>;
  assetFolders: AssetFolder[];
};

export const getAllRelevantTriples = ({
  componentTypeName,
  orgTriples,
  inferredTriples,
  models,
  workspaces,
  permissionContext,
  assetFolders,
}: GetAllRelevantTriplesArgs): Record<string, TripleWithContext> => {
  if (!componentTypeName) return {};

  const relevantOrgTriples = orgTriples
    .filter(
      ({ sourceType, targetType }) =>
        sourceType === componentTypeName || targetType === componentTypeName
    )
    .flatMap(triple => {
      const directedTriple = {
        direction: (triple.sourceType === componentTypeName
          ? 'outgoing'
          : 'incoming') as TripleDirection,
        ...triple,
      };
      return getTripleAndAddSymmetricTripleIfNeeded(directedTriple);
    });

  const relevantInferredTriples = inferredTriples
    .filter(
      ({ sourceComponentType, targetComponentType }) =>
        sourceComponentType === componentTypeName ||
        targetComponentType === componentTypeName
    )
    .flatMap(triple => {
      const directedTriple: DirectedTriple = {
        direction:
          triple.sourceComponentType === componentTypeName
            ? 'outgoing'
            : 'incoming',
        sourceType: triple.sourceComponentType,
        referenceType: triple.referenceType,
        targetType: triple.targetComponentType,
      };

      return getTripleAndAddSymmetricTripleIfNeeded(directedTriple);
    });

  const relevantTriples = uniqBy(
    [...relevantOrgTriples, ...relevantInferredTriples],
    'tripleKey'
  );
  const triplesWithContext = toTriplesWithContext({
    triples: relevantTriples,
    models: models,
    workspaces,
    permissionContext,
    assetFolders,
  });

  return Object.fromEntries(
    triplesWithContext.map(triple => [triple.tripleKey!, triple])
  );
};

export const tripleToGroupKey = (triple: DirectedTriple) =>
  `${triple.direction}-${triple.sourceType}-${triple.referenceType}-${triple.targetType}`;

const getTripleAndAddSymmetricTripleIfNeeded = <T extends DirectedTriple>(
  directedTriple: T
): (T & { tripleKey: string })[] => {
  const isSymmetrical = directedTriple.sourceType === directedTriple.targetType;
  const maybeReverseTriple = isSymmetrical
    ? {
        ...directedTriple,
        direction: (directedTriple.direction === 'outgoing'
          ? 'incoming'
          : 'outgoing') as TripleDirection,
      }
    : null;
  const tripleWithKey = {
    ...directedTriple,
    tripleKey: tripleToGroupKey(directedTriple),
  };
  const reverseTripleWithKey = maybeReverseTriple
    ? {
        ...maybeReverseTriple,
        tripleKey: tripleToGroupKey(maybeReverseTriple),
      }
    : null;
  return reverseTripleWithKey
    ? [tripleWithKey, reverseTripleWithKey]
    : [tripleWithKey];
};
